import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './HelpPage.css';

const FAQ_ITEMS = [
  {
    category: 'Getting started',
    items: [
      {
        q: 'How do I add my first client?',
        a: 'From the dashboard click "Add new client" in the Manage Your Workspace section, or go to Client Management → Add Client. Fill in their name, email, and phone number. Once added you can start logging session notes and transcriptions immediately.',
      },
      {
        q: 'How do I schedule an appointment?',
        a: 'Go to Your Calendar and click any date to create an event. You can link events to a specific client, and set the duration. Your public booking link lets clients schedule directly into your calendar.',
      },
      {
        q: 'How do I share my booking link?',
        a: 'In Settings → Integrations → Public booking link, copy your unique URL. Share it via email, social media, or your website. Clients land on a clean booking page and their request appears in your calendar automatically.',
      },
    ],
  },
  {
    category: 'AI Persona',
    items: [
      {
        q: 'What is AI Persona?',
        a: 'AI Persona is your AI assistant for clients. It can conduct intake conversations, answer questions, and summarize sessions in your voice. You train it with your tone preferences and writing samples in Settings → AI settings.',
      },
      {
        q: 'How do I review AI conversations?',
        a: 'Go to AI Persona from the dashboard. Any conversations your AI had with clients that need your review appear with a "pending review" badge. You can approve, edit, or add notes before saving them to the client record.',
      },
      {
        q: 'Can I customize how the AI sounds?',
        a: 'Yes — in Settings → AI settings you can set a tone preference (professional, friendly, concise, formal), write a custom style prompt, and paste writing samples. The more context you give it, the more it sounds like you.',
      },
    ],
  },
  {
    category: 'Circles',
    items: [
      {
        q: 'What is a Circle?',
        a: 'A Circle is a shared team workspace. You invite colleagues, share clients with them, and everyone can see session notes, transcriptions, and AI summaries for shared clients. Great for practices with multiple practitioners.',
      },
      {
        q: 'Who pays for a Circle?',
        a: 'The Circle owner pays the subscription ($79–$199/mo depending on size). Members join for free — they just need their own AirFlyp account. Billing transfers automatically if you transfer ownership.',
      },
      {
        q: 'What happens if I share a client?',
        a: 'When you share a client to your Circle, all members can see that client\'s full record — session notes, transcriptions, AI summaries, and upcoming appointments. You stay the primary owner and can unshare at any time. Private clients are never visible to teammates.',
      },
      {
        q: 'Can I leave a Circle?',
        a: 'Yes — go to Circles → Manage → and click Leave. Your personal clients remain private. Any notes you added to shared clients stay attached to those clients. If you\'re the owner, you\'ll need to transfer ownership before leaving.',
      },
    ],
  },
  {
    category: 'Billing & account',
    items: [
      {
        q: 'What\'s included in the free plan?',
        a: 'The free plan includes up to 3 clients, 1 survey, and basic calendar features. AI Persona, unlimited clients, and Google Calendar sync require a Pro plan ($29/mo).',
      },
      {
        q: 'How do I upgrade to Pro?',
        a: 'From the dashboard click "Upgrade to Pro" or go to Settings → Manage plan. You\'ll be taken to our secure Stripe checkout. Your upgrade takes effect immediately.',
      },
      {
        q: 'How do I cancel my subscription?',
        a: 'Go to Settings → Manage plan → Cancel subscription. You\'ll keep Pro access until the end of your current billing period. Your data is never deleted — you can reactivate anytime.',
      },
      {
        q: 'Is my data secure?',
        a: 'Yes. All data is encrypted in transit (TLS) and at rest. Session notes and transcriptions are stored in your private account and never shared without your explicit action. We never sell your data.',
      },
    ],
  },
  {
    category: 'Mobile app',
    items: [
      {
        q: 'Is there a mobile app?',
        a: 'Yes — AirFlyp is available on iOS and Android. Search "AirFlyp" in the App Store or Google Play. The mobile app includes all core features: clients, calendar, AI Persona, and session notes.',
      },
      {
        q: 'Does the mobile app sync with the web?',
        a: 'Yes — everything syncs in real time. A note you add on mobile appears instantly on web and vice versa. Your calendar, clients, and Circle are the same across all devices.',
      },
    ],
  },
];

const HelpPage = () => {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState({});

  const toggle = (key) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="help-page">
      <div className="help-container">

        {/* Nav */}
        <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>

        {/* Header */}
        <div className="help-header">
          <h1>Help & Guide</h1>
          <p>Everything you need to get the most out of AirFlyp</p>
        </div>

        {/* Quick start cards */}
        <div className="help-quickstart">
          <h2 className="help-section-title">Get started in 4 steps</h2>
          <div className="quickstart-grid">
            <Link to="/createclient" className="quickstart-card">
              <div className="quickstart-num">1</div>
              <div className="quickstart-icon">👤</div>
              <h3>Add a client</h3>
              <p>Create your first client record with their contact details</p>
            </Link>
            <Link to="/calendar" className="quickstart-card">
              <div className="quickstart-num">2</div>
              <div className="quickstart-icon">🗓</div>
              <h3>Schedule a session</h3>
              <p>Click any date on your calendar to create an appointment</p>
            </Link>
            <Link to="/persona" className="quickstart-card">
              <div className="quickstart-num">3</div>
              <div className="quickstart-icon">✦</div>
              <h3>Set up AI Persona</h3>
              <p>Train your AI assistant with your tone and writing style</p>
            </Link>
            <Link to="/circles/create" className="quickstart-card">
              <div className="quickstart-num">4</div>
              <div className="quickstart-icon">✦✦</div>
              <h3>Create a Circle</h3>
              <p>Invite your team and start collaborating on shared clients</p>
            </Link>
          </div>
        </div>

        {/* Feature overview */}
        <div className="help-features">
          <h2 className="help-section-title">Key features</h2>
          <div className="features-overview-grid">
            <div className="feature-overview-card">
              <div className="feature-overview-icon">👥</div>
              <h3>Client management</h3>
              <p>Store client profiles, session notes, transcriptions, and AI summaries in one place. Every session is searchable and exportable.</p>
            </div>
            <div className="feature-overview-card">
              <div className="feature-overview-icon">🗓</div>
              <h3>Smart calendar</h3>
              <p>Schedule sessions, share your booking link, and sync with Google Calendar or any app that supports calendar subscriptions.</p>
            </div>
            <div className="feature-overview-card">
              <div className="feature-overview-icon" style={{color:'var(--accent)'}}>✦</div>
              <h3>AI Persona</h3>
              <p>Your AI assistant handles intake, answers client questions, and summarizes sessions — all in your voice and style.</p>
            </div>
            <div className="feature-overview-card dark">
              <div className="feature-overview-icon">✦✦</div>
              <h3>Circles</h3>
              <p>Share clients, notes, and AI summaries with your team. Up to 10 members per Circle — perfect for group practices.</p>
            </div>
            <div className="feature-overview-card">
              <div className="feature-overview-icon">📊</div>
              <h3>Surveys</h3>
              <p>Send intake surveys to clients before sessions. Responses are automatically attached to their client record.</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="help-faq">
          <h2 className="help-section-title">Frequently asked questions</h2>
          {FAQ_ITEMS.map((section) => (
            <div key={section.category} className="faq-section">
              <h3 className="faq-category">{section.category}</h3>
              {section.items.map((item, i) => {
                const key = `${section.category}-${i}`;
                return (
                  <div
                    key={key}
                    className={`faq-item ${openItems[key] ? 'open' : ''}`}
                  >
                    <button
                      className="faq-question"
                      onClick={() => toggle(key)}
                    >
                      <span>{item.q}</span>
                      <span className="faq-chevron">{openItems[key] ? '▴' : '▾'}</span>
                    </button>
                    {openItems[key] && (
                      <div className="faq-answer">{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="help-footer-cta">
          <p>Still have questions?</p>
          <a href="mailto:support@airflyp.com" className="help-contact-btn">
            Contact support →
          </a>
        </div>

      </div>
    </div>
  );
};

export default HelpPage;